import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import Loading from '../../../../shared/loading';
import {
  HelpBlock, Row, Textarea,
} from '../../common/form';
import FundContext from './context';
import { usePostQanda, usePutQanda } from '../../../../services/lwadmin/fund/fund-service';

const SRow = styled(Row)`
  flex-wrap: wrap;
  justify-content: flex-start;

  & > .loading-panel-loading {
    height: 24px !important;
  }

  &:after {
    order: 5;
    content: "";
    display: block;
    flex-basis: 100%;
    height: 0;
    border-bottom: 1px solid;
    border-image: ${props => `linear-gradient(45deg, white, ${props.theme.primaryColor}, white) 1`};
    margin: 10px 0
  }
`;

const STextarea = styled(Textarea)`
  flex-basis: 100%;
`;

const Category = styled.span`
  font: ${props => props.theme.fontWeightDccc} 14px/120% ${props => props.theme.fontFamilyBody};
  color: ${props => props.theme.secondaryColor};
  text-transform: uppercase;
  flex-basis: 100%;
`;

const Question = styled.p`
  font: ${props => props.theme.fontWeightBold} 16px/120% ${props => props.theme.fontFamilyBody};
  color: ${props => props.theme.bodyColor};
  flex-basis: 100%;
`;

export default function Answer({
  qandaId, questionId, category, question, answer,
}) {
  const { state, dispatch } = React.useContext(FundContext);
  const [qid, setQid] = React.useState(qandaId);
  const [{ loading: createLoading, error: crErr }, origCreate] = usePostQanda();
  const { callback: create } = useDebouncedCallback(origCreate, 500, { leading: true });
  const [{ loading: updateLoading, error: upErr }, origUpdate] = usePutQanda();
  const { callback: update } = useDebouncedCallback(origUpdate, 300, { leading: true });
  const loadingRef = React.useRef();

  React.useEffect(() => {
    loadingRef.current && loadingRef.current.toggle(createLoading || updateLoading);
  }, [createLoading, updateLoading]);

  const handleChange = e => {
    const { value } = e.target;
    const payload = { questionId, answer: _.trimStart(value) };
    dispatch({ type: 'setQandaAnswer', payload });
    if (state.fund.id) {
      if (qid) {
        update(state.fund.id, { ...payload, id: qid });
      } else {
        create(state.fund.id, payload).then(({ data }) => {
          const returnedId = _.get(data, 'id');
          if (returnedId) {
            dispatch({ type: 'setQandaIds', payload: [{ questionId, qandaId: returnedId }] });
            setQid(returnedId);
          }
        });
      }
    } // else we batch create in form.jsx after fund was created, which gives us the fund id
  };

  const error = crErr || upErr;

  return (
    <SRow key={`qanda-${questionId}`}>
      <Category>{category}</Category>
      <Question>{question}</Question>
      <STextarea name={`qa${questionId}`} value={answer || ''} onChange={handleChange} />
      {error && <HelpBlock>{_.get(error, 'response.data.answer')}</HelpBlock>}
      <Loading ref={loadingRef} variant="sip" />
    </SRow>
  );
}

Answer.propTypes = {
  questionId: PropTypes.number.isRequired,
  category: PropTypes.string.isRequired,
  question: PropTypes.string.isRequired,
  answer: PropTypes.string,
  qandaId: PropTypes.number,
};

Answer.defaultProps = {
  answer: '',
  qandaId: 0,
};
